import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mcpjam/design-system/button";
import { Input } from "@mcpjam/design-system/input";
import { Badge } from "@mcpjam/design-system/badge";
import { ScrollArea } from "@mcpjam/design-system/scroll-area";
import {
  FolderOpen,
  File,
  FileCode,
  RefreshCw,
  Eye,
  PanelLeftClose,
  Play,
  X,
} from "lucide-react";
import { EmptyState } from "./ui/empty-state";
import { ThreePanelLayout } from "./ui/three-panel-layout";
import { JsonEditor } from "@/components/ui/json-editor";
import { extractDisplayFromValue } from "@/components/chat-v2/shared/tool-result-text";
import type {
  MCPReadResourceResult,
  MCPResource,
  MCPResourceTemplate,
  MCPServerConfig,
} from "@mcpjam/sdk/browser";
import {
  listResources,
  readResource as readResourceApi,
} from "@/lib/apis/mcp-resources-api";
import type { ServerWithName } from "@/state/app-types";
import { listResourceTemplates } from "@/lib/apis/mcp-resource-templates-api";
import {
  CacheProvenanceBadge,
  type ServedFromCache,
} from "@/components/ui/cache-provenance-badge";
import { parseTemplate } from "url-template";
import { HOSTED_MODE } from "@/lib/config";
import type { ConnectionStatus } from "@/state/app-types";
import { boundedJsonByteLength } from "@/lib/webmcp/bounded-size";
import { useSurfaceAgentBridge } from "@/lib/webmcp/use-surface-agent-bridge";
import { createInspectorCommandClientError } from "@/lib/inspector-command-handlers";
import { clampText } from "@/lib/webmcp/groups/shared";
import { runWithScopeStepUp } from "@/lib/scope-step-up";
import type { ReadResourceInspectorCommand } from "@/shared/inspector-command.js";

/** Cap the list of primitives a snapshot enumerates (uris/names only). */
const RESOURCE_SNAPSHOT_MAX_ITEMS = 30;
/** Cap how many content blocks a read returns to the transcript. */
const RESOURCE_CONTENT_MAX_BLOCKS = 10;

/**
 * Build a transcript-safe view of a resource body: each text block is capped
 * with the group's `clampText`; binary/blob blocks are never inlined (metadata
 * + approximate size only). The whole tool result is additionally clamped by
 * `fromActionResult` in the group, so this is the inner, per-block bound.
 */
function capResourceContentForTranscript(content: unknown): {
  contentBlocks: unknown[];
  truncated: boolean;
} {
  const contents = Array.isArray((content as any)?.contents)
    ? ((content as any).contents as any[])
    : [];
  let truncated = false;
  const contentBlocks = contents
    .slice(0, RESOURCE_CONTENT_MAX_BLOCKS)
    .map((c) => {
      if (typeof c?.text === "string") {
        const capped = clampText(c.text);
        if (capped !== c.text) truncated = true;
        return { uri: c?.uri, mimeType: c?.mimeType, text: capped };
      }
      const approxBase64Length =
        typeof c?.blob === "string" ? c.blob.length : undefined;
      return {
        uri: c?.uri,
        mimeType: c?.mimeType,
        note: "Binary content omitted for the transcript.",
        ...(approxBase64Length !== undefined ? { approxBase64Length } : {}),
      };
    });
  if (contents.length > RESOURCE_CONTENT_MAX_BLOCKS) truncated = true;
  return { contentBlocks, truncated };
}

interface ResourcesTabProps {
  serverConfig?: MCPServerConfig;
  serverName?: string;
  /**
   * The resolved server entry, needed to drive a SEP-2350 scope step-up on a
   * `403 insufficient_scope` (its stored issuer + originally-granted scopes).
   * Optional: without it a read failure just surfaces the error.
   */
  server?: ServerWithName;
  serverConnectionStatus?: ConnectionStatus;
}

// RFC 6570 compliant URI template parameter extraction
function extractTemplateParameters(uriTemplate: string): string[] {
  const params = new Set<string>();
  const paramRegex = /\{[+#./;?&]?([^}]+)\}/g;
  let match;

  while ((match = paramRegex.exec(uriTemplate)) !== null) {
    const variables = match[1].replace(/^[+#./;?&]/, "").split(",");
    variables.forEach((v) => {
      const varName = v.split(":")[0].replace(/\*$/, "").trim();
      if (varName) params.add(varName);
    });
  }

  return Array.from(params);
}

/**
 * The variables an agent MUST supply to read a template. Only simple `{var}`
 * and reserved `{+var}` (path) expansions are required; query (`?`/`&`),
 * fragment (`#`), label (`.`) and path-segment (`/`/`;`) expansions omit
 * undefined variables cleanly under RFC 6570, so `search{?q,limit}` is readable
 * with only `q` — matching the on-screen Read flow (`buildParameters()` passes
 * only non-empty fields and `parseTemplate().expand()` drops the rest).
 */
function extractRequiredTemplateParameters(uriTemplate: string): string[] {
  const params = new Set<string>();
  const exprRegex = /\{([+#./;?&]?)([^}]+)\}/g;
  let match;

  while ((match = exprRegex.exec(uriTemplate)) !== null) {
    const operator = match[1];
    if (operator !== "" && operator !== "+") continue;
    match[2].split(",").forEach((v) => {
      const varName = v.split(":")[0].replace(/\*$/, "").trim();
      if (varName) params.add(varName);
    });
  }

  return Array.from(params);
}

// RFC 6570 compliant URI template expansion
function buildUriFromTemplate(
  uriTemplate: string,
  params: Record<string, string>,
): string {
  const template = parseTemplate(uriTemplate);
  return template.expand(params);
}

function renderResourceTextContent(
  text: string,
  preClassName: string,
  jsonWrapperClassName: string,
) {
  const display = extractDisplayFromValue(text);

  if (display?.kind === "json") {
    return (
      <div className={jsonWrapperClassName}>
        <JsonEditor
          value={display.value}
          readOnly
          showToolbar={false}
          height="100%"
        />
      </div>
    );
  }

  return (
    <pre className={preClassName}>
      {display?.kind === "text" ? display.text : text}
    </pre>
  );
}

export function ResourcesTab({
  serverConfig,
  serverName,
  server,
  serverConnectionStatus,
}: ResourcesTabProps) {
  const [activeTab, setActiveTab] = useState<"resources" | "templates">(
    "resources",
  );

  // Resources state
  const [resources, setResources] = useState<MCPResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<string>("");
  const [resourceContent, setResourceContent] =
    useState<MCPReadResourceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingResources, setFetchingResources] = useState(false);
  const [error, setError] = useState<string>("");
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [resourcesServedFromCache, setResourcesServedFromCache] = useState<
    ServedFromCache | undefined
  >(undefined);
  const [templatesServedFromCache, setTemplatesServedFromCache] = useState<
    ServedFromCache | undefined
  >(undefined);

  // Templates state
  const [templates, setTemplates] = useState<MCPResourceTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templateContent, setTemplateContent] =
    useState<MCPReadResourceResult | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [fetchingTemplates, setFetchingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string>("");
  const [templateOverrides, setTemplateOverrides] = useState<
    Record<string, string>
  >({});

  // Panel state
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const resourcesFetchVersionRef = useRef(0);
  const resourceReadVersionRef = useRef(0);
  const templatesFetchVersionRef = useRef(0);
  const templateReadVersionRef = useRef(0);
  const isServerConnected =
    serverConnectionStatus === undefined ||
    serverConnectionStatus === "connected";

  const selectedTemplateData = useMemo(() => {
    return templates.find((t) => t.uriTemplate === selectedTemplate) ?? null;
  }, [templates, selectedTemplate]);

  const templateParams = useMemo(() => {
    if (selectedTemplateData?.uriTemplate) {
      const paramNames = extractTemplateParameters(
        selectedTemplateData.uriTemplate,
      );
      return paramNames.map((name) => ({
        name,
        value: templateOverrides[name] ?? "",
      }));
    }
    return [];
  }, [selectedTemplateData?.uriTemplate, templateOverrides]);

  const resetLoadedResourceState = (
    invalidateRequests = true,
    stopLoading = true,
  ) => {
    if (invalidateRequests) {
      resourcesFetchVersionRef.current += 1;
      resourceReadVersionRef.current += 1;
      templatesFetchVersionRef.current += 1;
      templateReadVersionRef.current += 1;
    }
    if (stopLoading) {
      setLoading(false);
      setFetchingResources(false);
      setLoadingMore(false);
      setTemplateLoading(false);
      setFetchingTemplates(false);
    }
    setResources([]);
    setSelectedResource("");
    setResourceContent(null);
    setError("");
    setNextCursor(undefined);
    setTemplates([]);
    setSelectedTemplate("");
    setTemplateContent(null);
    setTemplateError("");
    setTemplateOverrides({});
    // SEP-2549 provenance describes the currently displayed lists; clear it
    // whenever those lists are emptied so a badge cannot outlive its data.
    setResourcesServedFromCache(undefined);
    setTemplatesServedFromCache(undefined);
  };

  // Fetch resources and templates on mount
  useEffect(() => {
    if (!serverConfig || !serverName || !isServerConnected) {
      resetLoadedResourceState();
      return;
    }
    fetchResources();
    if (!HOSTED_MODE) {
      fetchTemplates();
    }
  }, [serverConfig, serverName, isServerConnected]);

  useEffect(() => {
    if (HOSTED_MODE && activeTab === "templates") {
      setActiveTab("resources");
    }
  }, [activeTab]);

  const fetchResources = async (
    cursor?: string,
    append = false,
    forceRefresh = false,
  ) => {
    if (!serverName) return;
    if (!isServerConnected) {
      resetLoadedResourceState();
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setFetchingResources(true);
      setError("");
      setResources([]);
      setSelectedResource("");
      setResourceContent(null);
      setNextCursor(undefined);
      // Clear stale provenance at the start of a non-append (re)fetch so a
      // failed/in-flight refresh cannot keep showing the previous badge.
      setResourcesServedFromCache(undefined);
    }
    const fetchVersion = ++resourcesFetchVersionRef.current;

    try {
      const result = await listResources(serverName, cursor, {
        refresh: forceRefresh,
      });
      if (fetchVersion !== resourcesFetchVersionRef.current) return;
      const serverResources: MCPResource[] = Array.isArray(result.resources)
        ? result.resources
        : [];

      if (append) {
        setResources((prev) => [...prev, ...serverResources]);
      } else {
        setResources(serverResources);
        if (serverResources.length === 0) {
          setSelectedResource("");
          setResourceContent(null);
        } else if (
          !serverResources.some((resource) => resource.uri === selectedResource)
        ) {
          setResourceContent(null);
        }
      }
      setNextCursor(result.nextCursor);
      setResourcesServedFromCache(result.servedFromCache);
    } catch (err) {
      if (fetchVersion !== resourcesFetchVersionRef.current) return;
      setError(`Network error fetching resources: ${err}`);
    } finally {
      if (fetchVersion === resourcesFetchVersionRef.current) {
        setFetchingResources(false);
        setLoadingMore(false);
      }
    }
  };

  const fetchTemplates = async (forceRefresh = false) => {
    if (!serverName) return;
    if (!isServerConnected) {
      resetLoadedResourceState();
      return;
    }

    setFetchingTemplates(true);
    setTemplateError("");
    setTemplates([]);
    setSelectedTemplate("");
    setTemplateOverrides({});
    setTemplateContent(null);
    // Clear stale provenance at the start of a template (re)fetch so a
    // failed/in-flight refresh cannot keep showing the previous badge.
    setTemplatesServedFromCache(undefined);
    const fetchVersion = ++templatesFetchVersionRef.current;

    try {
      const serverTemplates = await listResourceTemplates(serverName, {
        refresh: forceRefresh,
      });
      if (fetchVersion !== templatesFetchVersionRef.current) return;
      setTemplates(serverTemplates);
      setTemplatesServedFromCache(serverTemplates.servedFromCache);
    } catch (err) {
      if (fetchVersion !== templatesFetchVersionRef.current) return;
      setTemplateError(`Could not fetch resource templates: ${err}`);
    } finally {
      if (fetchVersion === templatesFetchVersionRef.current) {
        setFetchingTemplates(false);
      }
    }
  };

  const loadMoreResources = useCallback(async () => {
    if (loadingMore) return;
    if (!nextCursor) return;

    await fetchResources(nextCursor, true);
  }, [nextCursor, loadingMore]);

  // Intersection observer for pagination
  useEffect(() => {
    if (!sentinelRef.current) return;

    const element = sentinelRef.current;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry.isIntersecting) return;
      if (!nextCursor || loadingMore) return;

      loadMoreResources();
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [nextCursor, loadingMore, loadMoreResources]);

  // Read resource
  const readResource = async (uri: string) => {
    if (!serverName) return;
    if (!isServerConnected) {
      setError("Connect this server before reading resources.");
      return;
    }
    setLoading(true);
    setError("");
    const readVersion = ++resourceReadVersionRef.current;

    try {
      // SEP-2350: the wrapper owns the whole step-up lifecycle — reset the
      // bounded budget on success, drive the union-scope re-authorization on a
      // `403 insufficient_scope`, re-throw everything else untouched.
      const data = await runWithScopeStepUp(server, () =>
        readResourceApi(serverName, uri),
      );
      if (readVersion !== resourceReadVersionRef.current) return;
      setResourceContent(data?.content ?? null);
    } catch (err) {
      if (readVersion !== resourceReadVersionRef.current) return;
      setError(`Error reading resource: ${err}`);
    } finally {
      if (readVersion === resourceReadVersionRef.current) {
        setLoading(false);
      }
    }
  };

  // Template parameter helpers
  const updateParamValue = (paramName: string, value: string) => {
    setTemplateOverrides((prev) => ({ ...prev, [paramName]: value }));
  };

  const buildParameters = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    templateParams.forEach((param) => {
      if (param.value !== "") {
        params[param.name] = param.value;
      }
    });
    return params;
  }, [templateParams]);

  const getResolvedUri = useCallback((): string => {
    if (!selectedTemplateData) return "";
    const params = buildParameters();
    return buildUriFromTemplate(selectedTemplateData.uriTemplate, params);
  }, [selectedTemplateData, buildParameters]);

  // Read template resource
  const readTemplateResource = useCallback(async () => {
    if (!selectedTemplate || !serverName) return;
    if (!isServerConnected) {
      setTemplateError("Connect this server before reading resources.");
      return;
    }

    setTemplateLoading(true);
    setTemplateError("");
    const readVersion = ++templateReadVersionRef.current;

    try {
      const uri = getResolvedUri();
      const data = await runWithScopeStepUp(server, () =>
        readResourceApi(serverName, uri),
      );
      if (readVersion !== templateReadVersionRef.current) return;
      setTemplateContent(data?.content ?? null);
    } catch (err) {
      if (readVersion !== templateReadVersionRef.current) return;
      setTemplateError(`Error reading resource: ${err}`);
    } finally {
      if (readVersion === templateReadVersionRef.current) {
        setTemplateLoading(false);
      }
    }
    // `server` is a dep because `runWithScopeStepUp` takes it: without it a
    // `403 insufficient_scope` on a template read could step up against a stale
    // (or `undefined`) server after the active server changed.
  }, [selectedTemplate, serverName, isServerConnected, getResolvedUri, server]);

  // Handle Enter key in template input fields
  const handleTemplateInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" && isServerConnected && !templateLoading) {
      e.preventDefault();
      readTemplateResource();
    }
  };

  // Handle Enter key to read resource globally
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.shiftKey) return;

      const target = e.target as HTMLElement;
      const tagName = target.tagName;
      const isEditable = target.isContentEditable;

      if (tagName === "INPUT" || tagName === "TEXTAREA" || isEditable) {
        return;
      }

      if (
        activeTab === "resources" &&
        selectedResource &&
        isServerConnected &&
        !loading
      ) {
        e.preventDefault();
        readResource(selectedResource);
      } else if (
        activeTab === "templates" &&
        selectedTemplate &&
        isServerConnected &&
        !templateLoading
      ) {
        e.preventDefault();
        readTemplateResource();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedResource,
    isServerConnected,
    loading,
    activeTab,
    selectedTemplate,
    templateLoading,
    readTemplateResource,
  ]);

  // Agent bridge: one mount-scoped tool (ui_read_resource) plus a redacted
  // snapshot. Registered here — ResourcesTab owns the resource/template lists
  // and the readResourceApi path the Read button uses. Must run before any
  // early return (rules of hooks); handlers throw when no server is selected.
  useSurfaceAgentBridge({
    surfaceId: "resources",
    handlers: {
      readResource: async (command) => {
        if (!serverName) {
          throw createInspectorCommandClientError(
            "unsupported_in_mode",
            "No server is selected on the Resources screen — select and connect one first.",
          );
        }
        if (!isServerConnected) {
          throw createInspectorCommandClientError(
            "unsupported_in_mode",
            "The selected server is not connected — connect it before reading resources.",
          );
        }
        const { payload } = command as ReadResourceInspectorCommand;
        const key = payload?.resource?.trim();
        if (!key) {
          throw createInspectorCommandClientError(
            "invalid_request",
            "'resource' is required (a resource uri/name or a template name/uriTemplate).",
          );
        }
        const args = payload.templateArguments ?? {};

        const concreteMatches = resources.filter(
          (r) => r.uri === key || r.name === key,
        );
        const templateMatches = templates.filter(
          (t) => t.uriTemplate === key || t.name === key,
        );
        const total = concreteMatches.length + templateMatches.length;
        if (total === 0) {
          throw createInspectorCommandClientError(
            "invalid_request",
            `No resource or template matches "${key}". Use a uri/name from this screen (list them with ui_snapshot_app).`,
          );
        }
        if (total > 1) {
          throw createInspectorCommandClientError(
            "invalid_request",
            `"${key}" is ambiguous — pass the exact resource uri (or template uriTemplate).`,
          );
        }

        let uri: string;
        let templateUriTemplate: string | null = null;
        if (concreteMatches.length === 1) {
          uri = concreteMatches[0].uri;
          setActiveTab("resources");
          setSelectedResource(uri);
        } else {
          const template = templateMatches[0];
          templateUriTemplate = template.uriTemplate;
          // Only path variables are required; optional query/fragment/etc.
          // expansions may be omitted, exactly as the on-screen Read allows.
          const requiredNames = extractRequiredTemplateParameters(
            template.uriTemplate,
          );
          const missing = requiredNames.filter(
            (name) => !args[name] || args[name].trim() === "",
          );
          if (missing.length > 0) {
            throw createInspectorCommandClientError(
              "invalid_request",
              `Template "${template.name}" needs templateArguments for: ${missing.join(", ")}.`,
            );
          }
          // Pass only the non-empty supplied values, like buildParameters();
          // expand() drops any omitted optional variable from the URI.
          const provided: Record<string, string> = {};
          for (const name of extractTemplateParameters(template.uriTemplate)) {
            const val = args[name];
            if (typeof val === "string" && val.trim() !== "") {
              provided[name] = val;
            }
          }
          uri = buildUriFromTemplate(template.uriTemplate, provided);
          setActiveTab("templates");
          setTemplateOverrides(provided);
          setSelectedTemplate(template.uriTemplate);
        }

        // Claim the read-version for the relevant pane BEFORE awaiting, using
        // the SAME refs the on-screen Read path bumps. A slower read (this one
        // or a concurrent UI/agent read) must not commit over a newer selection.
        const readVersion = templateUriTemplate
          ? ++templateReadVersionRef.current
          : ++resourceReadVersionRef.current;
        try {
          // SAME api AND the same step-up lifecycle as the Read button — an
          // agent-triggered `403 insufficient_scope` must be able to start a
          // re-authorization, and an agent-triggered success must clear the
          // budget, exactly as the on-screen path does.
          const data = await runWithScopeStepUp(server, () =>
            readResourceApi(serverName, uri),
          );
          const content = data?.content ?? null;
          // Only commit to the on-screen pane if this is still the newest read
          // for it; the tool still returns what IT fetched regardless.
          if (templateUriTemplate) {
            if (readVersion === templateReadVersionRef.current) {
              setTemplateContent(content);
            }
          } else if (readVersion === resourceReadVersionRef.current) {
            setResourceContent(content);
          }
          const { contentBlocks, truncated } =
            capResourceContentForTranscript(content);
          return {
            status: "resource_read",
            uri,
            ...(templateUriTemplate
              ? { uriTemplate: templateUriTemplate }
              : {}),
            truncated,
            contents: contentBlocks,
            note: truncated
              ? "Content was truncated for the transcript — read on screen for the full body."
              : undefined,
          };
        } catch (e) {
          throw createInspectorCommandClientError(
            "execution_failed",
            e instanceof Error ? e.message : "Failed to read the resource.",
          );
        }
      },
    },
    // Redacted STATE, not payloads: the selected server, the resource/template
    // NAMES+uris (bounded), the current selection, and whether a body was read
    // (presence/size only — never the content, which can be large or secret).
    snapshot: () => {
      // Pick the body for the ACTIVE tab: after reading a concrete resource and
      // then a template, a fixed `resourceContent ?? templateContent` would keep
      // reporting the stale concrete body while the Templates screen shows the
      // template one.
      const lastRead =
        activeTab === "templates"
          ? (templateContent ?? resourceContent)
          : (resourceContent ?? templateContent);
      let lastResultBytes = 0;
      let lastResultTruncated = false;
      if (lastRead) {
        // Bounded: never fully serialize a huge resource body just to size it.
        const sized = boundedJsonByteLength(lastRead);
        lastResultBytes = sized.bytes;
        lastResultTruncated = sized.truncated;
      }
      return {
        selectedServer: serverName ?? null,
        connected: isServerConnected,
        activeTab,
        resourceCount: resources.length,
        resources: resources
          .slice(0, RESOURCE_SNAPSHOT_MAX_ITEMS)
          .map((r) => ({ uri: r.uri, name: r.name })),
        templateCount: templates.length,
        templates: templates
          .slice(0, RESOURCE_SNAPSHOT_MAX_ITEMS)
          .map((t) => ({
            name: t.name,
            uriTemplate: t.uriTemplate,
            parameters: extractTemplateParameters(t.uriTemplate),
          })),
        selectedResource: selectedResource || null,
        selectedTemplate: selectedTemplate || null,
        selectedTemplateParameters: templateParams.map((p) => p.name),
        lastResult: {
          present: Boolean(lastRead),
          approxSizeBytes: lastResultBytes,
          ...(lastResultTruncated ? { approxSizeCapped: true } : {}),
        },
      };
    },
  });

  if (!serverConfig || !serverName) {
    return (
      <EmptyState
        icon={FolderOpen}
        title="No Server Selected"
        description="Connect to an MCP server to browse and explore its available resources."
      />
    );
  }

  const isFetching =
    activeTab === "resources" ? fetchingResources : fetchingTemplates;

  const sidebarContent = (
    <div className="h-full flex flex-col border-r border-border bg-background">
      {/* Header with tabs and actions */}
      <div className="border-b border-border flex-shrink-0">
        <div className="px-2 py-1.5 flex items-center gap-2">
          {/* Tabs */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setActiveTab("resources")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                activeTab === "resources"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Resources
              <span className="ml-1 text-[10px] font-mono opacity-70">
                {resources.length}
              </span>
            </button>
            {!HOSTED_MODE && (
              <button
                onClick={() => setActiveTab("templates")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                  activeTab === "templates"
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Templates
                <span className="ml-1 text-[10px] font-mono opacity-70">
                  {templates.length}
                </span>
              </button>
            )}
          </div>

          <CacheProvenanceBadge
            servedFromCache={
              activeTab === "resources"
                ? resourcesServedFromCache
                : templatesServedFromCache
            }
          />

          {/* Action buttons */}
          <div className="ml-auto flex items-center gap-0.5 text-muted-foreground/80">
            <Button
              onClick={() => {
                if (activeTab === "resources") {
                  fetchResources(undefined, false, true);
                } else {
                  fetchTemplates(true);
                }
              }}
              variant="ghost"
              size="sm"
              disabled={isFetching || !isServerConnected}
              className="h-7 w-7 p-0"
              title={
                activeTab === "resources"
                  ? "Refresh resources"
                  : "Refresh templates"
              }
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
              />
            </Button>
            <Button
              onClick={() => setIsSidebarVisible(false)}
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              title="Hide sidebar"
            >
              <PanelLeftClose className="h-3.5 w-3.5" />
            </Button>
          </div>

          {activeTab === "templates" && (
            <Button
              onClick={readTemplateResource}
              disabled={
                templateLoading || !selectedTemplate || !isServerConnected
              }
              size="sm"
              className="h-8 px-3 text-xs"
            >
              {templateLoading ? (
                <RefreshCw className="h-3 w-3 animate-spin" />
              ) : (
                <Play className="h-3 w-3" />
              )}
              <span className="ml-1">
                {templateLoading ? "Reading" : "Read Resource"}
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Content - Resources list, Templates list, or Template parameters */}
      {activeTab === "templates" && selectedTemplate ? (
        /* Template Parameters Form (in sidebar) */
        <div className="flex-1 flex flex-col min-h-0">
          {/* Template header */}
          <div className="bg-muted/30 flex-shrink-0 px-3 py-2">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => setSelectedTemplate("")}
                  className="hover:bg-muted/50 rounded px-1.5 py-0.5 -mx-1.5 transition-colors text-left"
                  title="Click to go back to list"
                >
                  <code className="text-xs font-mono font-medium text-foreground truncate block">
                    {selectedTemplateData?.name || selectedTemplate}
                  </code>
                </button>
                {selectedTemplateData?.description && (
                  <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                    {selectedTemplateData.description}
                  </p>
                )}
                {/* URI preview */}
                <p className="text-xs text-muted-foreground font-mono truncate mt-2">
                  {getResolvedUri() || selectedTemplate}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
                onClick={() => {
                  setSelectedTemplate("");
                  setTemplateOverrides({});
                  setTemplateContent(null);
                }}
                title="Clear selection"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Parameters */}
          <ScrollArea className="flex-1">
            <div className="px-3 py-3">
              {templateParams.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">
                  No parameters required
                </p>
              ) : (
                <div className="space-y-3">
                  {templateParams.map((param) => (
                    <div key={param.name} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs font-medium text-foreground">
                          {param.name}
                        </code>
                        <Badge
                          variant="outline"
                          className="text-[9px] px-1 py-0 border-amber-500/50 text-amber-600 dark:text-amber-400"
                        >
                          required
                        </Badge>
                      </div>
                      <Input
                        type="text"
                        value={param.value}
                        onChange={(e) =>
                          updateParamValue(param.name, e.target.value)
                        }
                        onKeyDown={handleTemplateInputKeyDown}
                        placeholder={`Enter ${param.name}`}
                        className="bg-background border-border text-xs h-8"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      ) : (
        /* Resources or Templates List */
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-2 pb-16">
              {activeTab === "resources" ? (
                /* Resources List */
                fetchingResources ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center mb-3">
                      <RefreshCw className="h-4 w-4 text-muted-foreground animate-spin" />
                    </div>
                    <p className="text-xs text-muted-foreground font-semibold mb-1">
                      Loading resources...
                    </p>
                  </div>
                ) : resources.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">
                      {isServerConnected
                        ? "No resources available"
                        : "Connect this server to load resources."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1">
                      {resources.map((resource) => (
                        <div
                          key={resource.uri}
                          className={`cursor-pointer transition-shadow duration-200 hover:bg-muted/30 dark:hover:bg-muted/50 p-3 rounded-md mx-2 ${
                            selectedResource === resource.uri
                              ? "bg-muted/50 dark:bg-muted/50 shadow-sm border border-border ring-1 ring-ring/20"
                              : "hover:shadow-sm"
                          }`}
                          onClick={() => {
                            setSelectedResource(resource.uri);
                            readResource(resource.uri);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <File className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <code className="font-mono text-xs font-medium text-foreground bg-muted px-1.5 py-0.5 rounded border border-border truncate">
                              {resource.name}
                            </code>
                          </div>
                          {resource.description && (
                            <p className="text-xs mt-2 line-clamp-2 leading-relaxed text-muted-foreground">
                              {resource.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    <div ref={sentinelRef} className="h-4" />
                    {loadingMore && (
                      <div className="flex items-center justify-center py-3 text-xs text-muted-foreground gap-2">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        <span>Loading more resources…</span>
                      </div>
                    )}
                    {!nextCursor && resources.length > 0 && !loadingMore && (
                      <div className="text-center py-3 text-xs text-muted-foreground">
                        No more resources
                      </div>
                    )}
                  </>
                )
              ) : /* Templates List */
              fetchingTemplates ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center mb-3">
                    <RefreshCw className="h-4 w-4 text-muted-foreground animate-spin" />
                  </div>
                  <p className="text-xs text-muted-foreground font-semibold mb-1">
                    Loading templates...
                  </p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    {isServerConnected
                      ? "No resource templates available"
                      : "Connect this server to load resource templates."}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {templates.map((template) => (
                    <div
                      key={template.uriTemplate}
                      className={`cursor-pointer transition-shadow duration-200 hover:bg-muted/30 dark:hover:bg-muted/50 p-3 rounded-md mx-2 ${
                        selectedTemplate === template.uriTemplate
                          ? "bg-muted/50 dark:bg-muted/50 shadow-sm border border-border ring-1 ring-ring/20"
                          : "hover:shadow-sm"
                      }`}
                      onClick={() => {
                        setTemplateOverrides({});
                        setSelectedTemplate(template.uriTemplate);
                        setTemplateContent(null);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <FileCode className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <code className="font-mono text-xs font-medium text-foreground bg-muted px-1.5 py-0.5 rounded border border-border truncate">
                          {template.name}
                        </code>
                      </div>
                      <p className="text-xs mt-1 line-clamp-1 leading-relaxed text-muted-foreground font-mono">
                        {template.uriTemplate}
                      </p>
                      {template.description && (
                        <p className="text-xs mt-2 line-clamp-2 leading-relaxed text-muted-foreground">
                          {template.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );

  const resourcesCenterContent = (
    <div className="h-full flex flex-col bg-background">
      {error ? (
        <div className="p-4">
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-xs font-medium">
            {error}
          </div>
        </div>
      ) : loading ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
            </div>
            <p className="text-xs font-semibold text-foreground mb-1">
              Loading resource...
            </p>
          </div>
        </div>
      ) : resourceContent ? (
        <div className="flex-1 min-h-0 p-4 flex flex-col">
          {resourceContent?.contents?.map((content: any, index: number) => (
            <div key={index} className="flex-1 min-h-0">
              {content.type === "text" ? (
                renderResourceTextContent(
                  content.text,
                  "h-full text-xs font-mono whitespace-pre-wrap p-4 bg-muted/30 border border-border rounded-md overflow-auto",
                  "h-full",
                )
              ) : (
                <div className="h-full">
                  <JsonEditor
                    value={content}
                    readOnly
                    showToolbar={false}
                    height="100%"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <Eye className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs font-semibold text-foreground mb-1">
              No selection
            </p>
            <p className="text-xs text-muted-foreground font-medium">
              Select a resource from the sidebar
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const templatesCenterContent = (
    <div className="h-full flex flex-col bg-background">
      {templateError ? (
        <div className="p-4">
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-xs font-medium">
            {templateError}
          </div>
        </div>
      ) : templateLoading ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
            </div>
            <p className="text-xs font-semibold text-foreground mb-1">
              Loading resource...
            </p>
          </div>
        </div>
      ) : templateContent ? (
        <div className="flex-1 min-h-0 p-4 flex flex-col">
          {templateContent?.contents?.map((content: any, index: number) => (
            <div key={index} className="flex-1 min-h-0">
              {content.type === "text" ? (
                renderResourceTextContent(
                  content.text,
                  "h-full text-xs font-mono whitespace-pre-wrap p-4 bg-muted/30 border border-border rounded-md overflow-auto",
                  "h-full",
                )
              ) : (
                <div className="h-full">
                  <JsonEditor
                    value={content}
                    readOnly
                    showToolbar={false}
                    height="100%"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <Eye className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs font-semibold text-foreground mb-1">
              {selectedTemplate ? "Ready to read" : "Select a template"}
            </p>
            <p className="text-xs text-muted-foreground font-medium">
              {selectedTemplate
                ? "Click Read Resource to view content"
                : "Choose a resource template from the sidebar"}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ThreePanelLayout
      id="resources"
      sidebar={sidebarContent}
      content={
        activeTab === "templates"
          ? templatesCenterContent
          : resourcesCenterContent
      }
      sidebarVisible={isSidebarVisible}
      onSidebarVisibilityChange={setIsSidebarVisible}
      sidebarTooltip="Show resources sidebar"
      serverName={serverName}
    />
  );
}
